// Ticket #336: Panel del Party extraído del play/index.js para evitar
// que el render de party state invalide el render del InventoryBox
// entero (que incluye el grid de 24 slots del inv).
//
// React.memo con comparador shallow nativo — el parent ya memoiza el
// elemento JSX vía `_getCachedPartyPanelEl`, así que la mayoría de las
// veces ni siquiera se invoca este componente. Solo cuando cambia:
//   - `party` (ref del state.party — recreado en messages.js:partyState)
//   - `partyInviteName` (input controlado)
//   - `myName` (estable)
//
// El parent re-renderea decenas de veces por segundo en combate;
// memorizar acá ahorra ~15-45ms/seg de reconciliación React.

import React from "react";
import { nameClases, nameRazas } from "../../config/config";

function PartyPanel({
    style,
    party,
    partyInviteName,
    myName,
    mapNames,
    onInviteNameChange,
    onSendDialog,
    onMemberContextMenu
}) {
    const mapName = idMap => {
        if (!idMap) return "—";
        const n = mapNames && mapNames[String(idMap)];
        return n || ("M" + idMap);
    };

    return (
        <div className={style.partyPanel}>
            <div className={style.partyPanelHeader}>
                <span className={style.partyPanelTitle}>Party</span>
                <span className={style.partyPanelRule} />
                {party && (
                    <span className={style.partyPanelCount}>
                        {party.members.length} miembros
                    </span>
                )}
            </div>

            {party ? (
                <div className={style.partyMembersList}>
                    {party.members.map((m, idx) => {
                        const cls = nameClases[m.idClase] || "—";
                        const rz = nameRazas[m.idRaza] || "—";
                        const dead = m.connected && m.hp <= 0 && m.maxHp > 0;
                        const hpPct = m.maxHp > 0
                            ? Math.max(0, Math.min(100, (m.hp / m.maxHp) * 100))
                            : 0;
                        const mpPct = m.maxMana > 0
                            ? Math.max(0, Math.min(100, (m.mana / m.maxMana) * 100))
                            : 0;
                        // Ticket #336: color directo del server — refleja
                        // GM toggle, faction Armada (#00BFFF) / Caos
                        // (#8B0000), criminal (red), civil (#3333ff).
                        const nameColor = m.color || "#3333ff";
                        const isMe = myName && m.name === myName;
                        // Ticket #336 polish: estado visual del 👤 + 👑.
                        // Inline `style` (no CSS classes) para no depender de
                        // CSS modules / :global() — siempre gana en specificity,
                        // funciona en cualquier scope del DOM. 3 estados:
                        //   - Muerto → silueta blanca (brightness(0) invert(1))
                        //   - Desconectado o fuera de rango XP → grayscale gris
                        //   - Conectado + vivo + en rango → normal (color font)
                        let stateStyle = null;
                        let stateTitle = "En rango";
                        if (!m.connected) {
                            stateStyle = { filter: "grayscale(1) brightness(0.75)", opacity: 0.65 };
                            stateTitle = "Desconectado";
                        } else if (dead) {
                            stateStyle = { filter: "brightness(0) invert(1)", opacity: 0.85 };
                            stateTitle = "Muerto";
                        } else if (m.inXpRange === false) {
                            stateStyle = { filter: "grayscale(1) brightness(0.75)", opacity: 0.65 };
                            stateTitle = "Fuera de rango de XP";
                        }
                        const crown = m.isLeader ? (
                            <span
                                className={style.partyLeaderCrown}
                                style={stateStyle || undefined}
                                title="Líder del party"
                            >👑</span>
                        ) : null;
                        const presence = (
                            <span style={stateStyle || undefined} title={stateTitle}>
                                👤
                            </span>
                        );
                        // El × del action cell SIEMPRE presente (kick/salir
                        // según contexto). Disconnected NO bloquea la acción:
                        // el leader puede expulsar al ghost si quiere limpiar
                        // el party. Yo (isMe) sale; otro y soy leader → kick.
                        let xEl;
                        if (isMe) {
                            xEl = (
                                <span
                                    className={style.partyKickX}
                                    title="Salir de la party"
                                    onClick={() => onSendDialog("/p salir")}
                                >×</span>
                            );
                        } else if (party.iAmLeader && !m.isLeader) {
                            xEl = (
                                <span
                                    className={style.partyKickX}
                                    title={"Expulsar " + m.name}
                                    onClick={() => {
                                        onSendDialog(
                                            /\s/.test(m.name)
                                                ? '/p kick "' + m.name + '"'
                                                : "/p kick " + m.name
                                        );
                                    }}
                                >×</span>
                            );
                        } else {
                            xEl = <span className={style.partyKickX} style={{ visibility: "hidden" }}>×</span>;
                        }
                        // Right-click sobre la row — solo se habilita si
                        // soy el líder y el target es un miembro DISTINTO
                        // que está ONLINE. Si esos checks fallan, el handler
                        // simplemente no abre el menu (no preventDefault →
                        // el browser muestra su menu nativo, igual que en
                        // las rows del inventario antes de #207).
                        const canPromote =
                            party.iAmLeader &&
                            m.connected &&
                            !m.isLeader &&
                            !isMe;
                        const onRowCtxMenu = canPromote && onMemberContextMenu
                            ? e => onMemberContextMenu(e, m.name)
                            : undefined;
                        return (
                            <div
                                key={idx}
                                className={`${style.partyRow} ${
                                    !m.connected ? style.partyRowDisconnected : ""
                                } ${dead ? style.partyRowDead : ""}`}
                                onContextMenu={onRowCtxMenu}
                                title={canPromote ? "Click derecho: promover a líder" : undefined}
                            >
                                <span className={style.partyColLvl}>
                                    {m.level || "?"}
                                </span>
                                <span
                                    className={style.partyColName}
                                    style={{ color: nameColor }}
                                    title={m.name}
                                >
                                    {m.name}
                                </span>
                                <div className={style.partyColStat}>
                                    <div className={style.partyStatLine}>
                                        <div className={style.partyBar}>
                                            <div
                                                className={style.partyBarFillHp}
                                                style={{ width: hpPct + "%" }}
                                            />
                                        </div>
                                        <span className={style.partyBarNum}>
                                            {m.connected && m.maxHp > 0
                                                ? `${m.hp}/${m.maxHp}`
                                                : "—"}
                                        </span>
                                    </div>
                                    <div className={style.partyStatLine}>
                                        <div className={style.partyBar}>
                                            <div
                                                className={style.partyBarFillMp}
                                                style={{ width: mpPct + "%" }}
                                            />
                                        </div>
                                        <span className={style.partyBarNum}>
                                            {m.connected && m.maxMana > 0
                                                ? `${m.mana}/${m.maxMana}`
                                                : "—"}
                                        </span>
                                    </div>
                                </div>
                                <span className={style.partyColClassRace} title={cls + "/" + rz}>
                                    {cls.slice(0, 4)}/{rz.slice(0, 4)}
                                </span>
                                <span className={style.partyActionCell}>
                                    {crown}
                                    {presence}
                                    {xEl}
                                </span>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className={style.partyEmpty}>
                    Sin party. Invitá a alguien para formar una.
                </div>
            )}

            {(!party || party.iAmLeader) && (
                <div className={style.partyInviteRow}>
                    <input
                        type="text"
                        className={style.partyInviteInput}
                        value={partyInviteName || ""}
                        onChange={e => onInviteNameChange(e.target.value)}
                        onKeyDown={e => {
                            if (e.keyCode === 13) {
                                const name = (partyInviteName || "").trim();
                                if (!name) return;
                                const cmd = /\s/.test(name)
                                    ? '/p invitar "' + name + '"'
                                    : "/p invitar " + name;
                                onSendDialog(cmd);
                                onInviteNameChange("");
                            }
                        }}
                        placeholder="Nombre"
                        maxLength={30}
                    />
                    <button
                        type="button"
                        className={style.partyInviteBtn}
                        onClick={() => {
                            const name = (partyInviteName || "").trim();
                            if (!name) return;
                            const cmd = /\s/.test(name)
                                ? '/p invitar "' + name + '"'
                                : "/p invitar " + name;
                            onSendDialog(cmd);
                            onInviteNameChange("");
                        }}
                    >
                        Invitar
                    </button>
                </div>
            )}
        </div>
    );
}

// React.memo con shallow compare nativo. Props son primitivos (string)
// o refs estables (party desde state, callbacks bound de la clase).
export default React.memo(PartyPanel);
